import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Users, RefreshCw, Loader2, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react'
import { clsx } from 'clsx'

// ── Persona types ────────────────────────────────────────────────────────────

interface PersonaMeta {
  id: string
  name: string
  style: string
  color: string
  initials: string
  description: string
}

interface PersonaResult {
  agent_id: string
  action: string
  confidence: number
  title: string
  reasoning: string
  persona_name: string
  persona_style: string
  persona_color: string
  persona_initials: string
  data: {
    top_picks?: string[]
    avoid?: string[]
    risk_view?: string
    time_horizon?: string
    key_metric?: string
  }
}

// ── Color maps ───────────────────────────────────────────────────────────────

const PERSONA_BG: Record<string, string> = {
  green:  'bg-emerald-500',
  blue:   'bg-blue-500',
  gold:   'bg-amber-500',
  purple: 'bg-purple-500',
  pink:   'bg-pink-500',
  red:    'bg-rose-500',
}

const PERSONA_BORDER: Record<string, string> = {
  green:  'border-emerald/30',
  blue:   'border-blue/30',
  gold:   'border-amber/30',
  purple: 'border-purple/30',
  pink:   'border-pink/30',
  red:    'border-rose/30',
}

const PERSONA_TEXT: Record<string, string> = {
  green:  'text-emerald',
  blue:   'text-blue-light',
  gold:   'text-amber',
  purple: 'text-purple-light',
  pink:   'text-pink-400',
  red:    'text-rose-light',
}

const ACTION_ICON: Record<string, React.ReactNode> = {
  BUY:   <TrendingUp size={14} className="text-emerald" aria-hidden />,
  SELL:  <TrendingDown size={14} className="text-rose-light" aria-hidden />,
  HOLD:  <Minus size={14} className="text-blue-light" aria-hidden />,
  ALERT: <AlertTriangle size={14} className="text-amber" aria-hidden />,
}

const ACTION_BADGE: Record<string, string> = {
  BUY:   'badge-green',
  SELL:  'badge-red',
  HOLD:  'badge-blue',
  ALERT: 'badge-amber',
  INFO:  'badge-blue',
  AVOID: 'badge-red',
}

// ── Avatar component ─────────────────────────────────────────────────────────

function PersonaAvatar({ initials, color, size = 'md' }: { initials: string; color: string; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'w-14 h-14 text-lg' : size === 'md' ? 'w-10 h-10 text-sm' : 'w-7 h-7 text-[10px]'
  return (
    <div className={clsx(
      'rounded-full flex items-center justify-center font-bold text-white shrink-0',
      PERSONA_BG[color] ?? 'bg-surface-hover',
      sizeClass,
    )}>
      {initials}
    </div>
  )
}

// ── Persona Card ─────────────────────────────────────────────────────────────

function PersonaCard({ persona }: { persona: PersonaMeta }) {
  return (
    <div className={clsx('card card-hover p-5 flex flex-col gap-3 border', PERSONA_BORDER[persona.color] ?? 'border-border')}>
      <div className="flex items-center gap-3">
        <PersonaAvatar initials={persona.initials} color={persona.color} size="md" />
        <div>
          <h3 className="text-sm font-bold text-text-primary">{persona.name}</h3>
          <p className={clsx('text-xs font-medium', PERSONA_TEXT[persona.color] ?? 'text-text-muted')}>
            {persona.style}
          </p>
        </div>
      </div>
      <p className="text-sm text-text-secondary leading-relaxed">{persona.description}</p>
    </div>
  )
}

// ── Result Card (after analysis) ─────────────────────────────────────────────

function PersonaResultCard({ result }: { result: PersonaResult }) {
  const color = result.persona_color
  return (
    <div className={clsx('card card-hover p-5 flex flex-col gap-3 border', PERSONA_BORDER[color] ?? 'border-border')}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <PersonaAvatar initials={result.persona_initials} color={color} />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-text-primary">{result.persona_name}</h3>
          <p className={clsx('text-xs font-medium', PERSONA_TEXT[color] ?? 'text-text-muted')}>
            {result.persona_style}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {ACTION_ICON[result.action]}
          <span className={clsx('badge text-xs', ACTION_BADGE[result.action] ?? 'badge-blue')}>
            {result.action}
          </span>
        </div>
      </div>

      {/* Title */}
      <h4 className="text-sm font-semibold text-text-primary leading-snug">{result.title}</h4>

      {/* Confidence bar */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-muted">Confidence:</span>
        <div className="flex-1 h-1.5 bg-surface-hover rounded-full">
          <div
            className={clsx(
              'h-full rounded-full',
              result.confidence >= 70 ? 'bg-emerald' : result.confidence >= 40 ? 'bg-amber' : 'bg-rose-light',
            )}
            style={{ width: `${result.confidence}%` }}
          />
        </div>
        <span className="text-xs font-semibold text-text-primary">{result.confidence}%</span>
      </div>

      {/* Reasoning */}
      <p className="text-sm text-text-secondary leading-relaxed">{result.reasoning}</p>

      {/* Metadata row */}
      <div className="flex flex-wrap gap-2">
        {result.data?.risk_view && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-surface-hover text-text-muted">
            Risk: {result.data.risk_view}
          </span>
        )}
        {result.data?.time_horizon && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-surface-hover text-text-muted">
            Horizon: {result.data.time_horizon}
          </span>
        )}
        {result.data?.key_metric && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-surface-hover text-text-muted">
            Watching: {result.data.key_metric}
          </span>
        )}
      </div>

      {/* Picks & avoids */}
      {(result.data?.top_picks?.length || result.data?.avoid?.length) ? (
        <div className="flex gap-4 text-xs">
          {result.data.top_picks?.length ? (
            <div>
              <span className="text-text-muted">Picks: </span>
              <span className="font-medium text-emerald">{result.data.top_picks.join(', ')}</span>
            </div>
          ) : null}
          {result.data.avoid?.length ? (
            <div>
              <span className="text-text-muted">Avoid: </span>
              <span className="font-medium text-rose-light">{result.data.avoid.join(', ')}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function PersonasPage() {
  const [personas, setPersonas] = useState<PersonaMeta[]>([])
  const [results, setResults] = useState<PersonaResult[]>([])
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchPersonas()
  }, [])

  async function fetchPersonas() {
    try {
      setLoading(true)
      const { data } = await api.get('/api/personas')
      setPersonas(data)
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to load personas')
    } finally {
      setLoading(false)
    }
  }

  async function runAnalysis() {
    try {
      setAnalyzing(true)
      setError('')
      const { data } = await api.post('/api/personas/analyze')
      setResults(data.personas ?? [])
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Analysis failed')
    } finally {
      setAnalyzing(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-8 w-72 skeleton" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="h-40 skeleton" />)}
        </div>
        <p className="text-text-muted text-sm text-center">Loading personas...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Users size={20} className="text-blue-light" aria-hidden />
            Fund Manager Personas
          </h1>
          <p className="text-sm text-text-secondary">
            AI agents that think like the world's best investors — analysing your portfolio through different lenses
          </p>
        </div>
        <button
          onClick={runAnalysis}
          disabled={analyzing}
          className="btn-primary gap-1.5 text-xs"
        >
          {analyzing
            ? <><Loader2 size={13} className="animate-spin" aria-hidden /> Analysing...</>
            : <><RefreshCw size={13} aria-hidden /> Run Analysis</>
          }
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5">
          <p className="text-sm text-rose-light">{error}</p>
        </div>
      )}

      {/* Results (debate view) */}
      {results.length > 0 && (
        <div className="space-y-4">
          <h2 className="panel-title">The Debate</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 stagger-children">
            {results.map(r => (
              <PersonaResultCard key={r.agent_id} result={r} />
            ))}
          </div>
        </div>
      )}

      {/* Persona cards (always shown) */}
      <div className="space-y-4">
        <h2 className="panel-title">Investment Philosophies</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 stagger-children">
          {personas.map(p => (
            <PersonaCard key={p.id} persona={p} />
          ))}
        </div>
      </div>
    </div>
  )
}
